import { getSubscriptionURLIsRedirected, getSubscriptionUUID } from '@/api';
import { Device, Logger, Sound } from '@/classes';
import { API_URL, API_VERSION, SECOND_MS } from '@/constants';
import { useExtendFocusable } from '@/hooks/useExtendFocusable';
import { useFocusElement } from '@/hooks/useFocusElement';
import { setFocus } from '@noriginmedia/norigin-spatial-navigation';
import { QRCodeSVG } from 'qrcode.react';
import { stringify } from 'qs';
import { KeyboardEventHandler, useCallback, useEffect, useMemo, useState } from 'react';

const POPUP_IMAGES: Record<string, Record<string, string>> = {
  ko: {
    part1: require('@/assets/modal/ko-subscription-ad-1.png'),
    part2: require('@/assets/modal/ko-subscription-ad-2.png'),
    promotionPart1: require('@/assets/modal/ko-promotion-ad-1.png'),
    promotionPart2: require('@/assets/modal/ko-promotion-ad-2.png'),
  },
  en: {
    part1: require('@/assets/modal/en-subscription-ad-1.png'),
    part2: require('@/assets/modal/en-subscription-ad-2.png'),
    promotionPart1: require('@/assets/modal/en-promotion-ad-1.png'),
    promotionPart2: require('@/assets/modal/en-promotion-ad-2.png'),
  },
  es: {
    part1: require('@/assets/modal/es-subscription-ad-1.png'),
    part2: require('@/assets/modal/es-subscription-ad-2.png'),
    promotionPart1: require('@/assets/modal/es-promotion-ad-1.png'),
    promotionPart2: require('@/assets/modal/es-promotion-ad-2.png'),
  },
  ja: {
    part1: require('@/assets/modal/ja-subscription-ad-1.png'),
    part2: require('@/assets/modal/ja-subscription-ad-2.png'),
    promotionPart1: require('@/assets/modal/ja-promotion-ad-1.png'),
    promotionPart2: require('@/assets/modal/ja-promotion-ad-2.png'),
  },
  zh: {
    part1: require('@/assets/modal/zh-subscription-ad-1.png'),
    part2: require('@/assets/modal/zh-subscription-ad-2.png'),
    promotionPart1: require('@/assets/modal/zh-promotion-ad-1.png'),
    promotionPart2: require('@/assets/modal/zh-promotion-ad-2.png'),
  },
  pt: {
    part1: require('@/assets/modal/pt-subscription-ad-1.png'),
    part2: require('@/assets/modal/pt-subscription-ad-2.png'),
    promotionPart1: require('@/assets/modal/pt-promotion-ad-1.png'),
    promotionPart2: require('@/assets/modal/pt-promotion-ad-2.png'),
  },
  id: {
    part1: require('@/assets/modal/id-subscription-ad-1.png'),
    part2: require('@/assets/modal/id-subscription-ad-2.png'),
    promotionPart1: require('@/assets/modal/id-promotion-ad-1.png'),
    promotionPart2: require('@/assets/modal/id-promotion-ad-2.png'),
  },
};

const CloseButton = ({ onClick }: { onClick: () => void }) => {
  const { ref, focused } = useExtendFocusable({ focusKey: 'close-button' });
  const handleClick = useCallback(() => {
    Sound.press();
    if (onClick) {
      onClick();
    }
  }, [onClick]);

  useFocusElement({ ref, focused, onClick: handleClick, keyCode: 13, propagate: false });

  return (
    <button
      ref={ref}
      className="rounded-[15px] ease-in-out duration-300 outline-disable outline-low-secondary-4 bg-transparent"
      style={{
        position: 'absolute',
        right: '44px',
        bottom: '20px',
        width: '172px',
        height: '58px',
      }}
      onClick={handleClick}
      onContextMenu={handleClick}
    ></button>
  );
};

export const SubscribeModalContent = ({
  sourcePage,
  onClose,
  promotionCode,
}: {
  sourcePage: string;
  onClose: (scannedSubscriptionURL: boolean) => void;
  promotionCode?: string;
}) => {
  const { ref } = useExtendFocusable({ isFocusBoundary: true });
  const [uuid, setUuid] = useState('');
  const [error, setError] = useState(false);
  const [isScanned, setIsScanned] = useState(false);

  const url = useMemo(() => {
    if (uuid) {
      const language = Device.language === 'ko' ? 'ko' : Device.language === 'ja' ? 'ja' : 'en';
      const redirectURL = `${API_URL}/api/${API_VERSION}/membership/subscriptions/redirect?${stringify({
        uuid,
        language,
        utm_source: Device.utm_source,
        promotion_code: promotionCode,
      })}`;

      if (process.env.NODE_ENV === 'development') {
        console.info('Redirect URL', redirectURL);
      }

      return redirectURL;
    }
  }, [promotionCode, uuid]);

  const loadSubscriptionURL = useCallback(async () => {
    try {
      const uuid = await getSubscriptionUUID();
      setUuid(uuid);
    } catch (e) {
      console.error(e);
      setError(true);
    }
  }, []);

  const handleEvent = useCallback<KeyboardEventHandler<HTMLDivElement>>(
    (event) => {
      const isPressedCloseButton =
        event.keyCode === 10009 ||
        event.keyCode === 461 ||
        event.keyCode === 13 ||
        event.code === 'Backspace' ||
        event.code === 'Escape' ||
        event.code === 'Enter' ||
        event.key === 'Backspace' ||
        event.key === 'Escape' ||
        event.key === 'Enter';

      event.stopPropagation();
      event.preventDefault();
      if (isPressedCloseButton) {
        Sound.press();
        onClose(isScanned);
      } else {
        setFocus('close-button');
      }
    },
    [onClose, isScanned],
  );

  const handleClose = useCallback(() => {
    onClose(isScanned);
  }, [onClose, isScanned]);

  useEffect(() => {
    loadSubscriptionURL();
  }, [loadSubscriptionURL]);

  useEffect(() => {
    if (uuid) {
      const interval = setInterval(async () => {
        try {
          const isRedirected = await getSubscriptionURLIsRedirected();
          if (isRedirected) {
            Logger.clickSubscriptionPopup(sourcePage, !!promotionCode);
            setIsScanned(true);
            clearInterval(interval);
          } else {
            setIsScanned(false);
          }
        } catch (e) {
          console.error(e);
        }
      }, 5 * SECOND_MS);

      return () => {
        clearInterval(interval);
      };
    }
  }, [promotionCode, sourcePage, uuid]);

  useEffect(() => {
    setFocus('close-button');
    Logger.exposedSubscriptionPopup(sourcePage, !!promotionCode);
  }, [promotionCode, sourcePage]);

  const partAImageSrc = useMemo(() => {
    if (promotionCode) {
      return POPUP_IMAGES[Device.language].promotionPart1;
    }

    return POPUP_IMAGES[Device.language].part1;
  }, [promotionCode]);

  const partBImageSrc = useMemo(() => {
    if (promotionCode) {
      return POPUP_IMAGES[Device.language].promotionPart2;
    }

    return POPUP_IMAGES[Device.language].part2;
  }, [promotionCode]);

  return (
    <div
      ref={ref}
      tabIndex={999}
      className="relative w-[1600px] h-[900px] text-[#000000] outline-disable overflow-hidden rounded-[30px]"
      onKeyDown={handleEvent}
    >
      {!isScanned && (
        <>
          <img src={partAImageSrc} className="w-full h-auto" />
          <div className="absolute inline-block right-[193px] top-[366px] w-[365px] h-[365px] bg-white rounded-[10px] p-[20px] m-auto">
            {url && !error && <QRCodeSVG style={{ zIndex: 1 }} value={url} width="100%" height="100%" />}
            {error && (
              <div className="w-full h-full flex justify-center items-center">
                <svg width="200" height="200" viewBox="0 0 60 60" fill="none">
                  <circle cx="30" cy="30" r="28" stroke="#F87171" strokeWidth="4" fill="#FEE2E2" />
                  <path d="M20 20 L40 40 M40 20 L20 40" stroke="#F87171" strokeWidth="4" strokeLinecap="round" />
                </svg>
              </div>
            )}
          </div>
        </>
      )}
      {isScanned && <img src={partBImageSrc} className="w-full h-auto" />}

      <CloseButton onClick={handleClose} />
    </div>
  );
};
